import Image from 'next/image';
import { useState } from 'react';
import validator from 'validator';
import { sendEmail } from '../../lib/api';

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  category: string;
  message: string;
  marketingConsent: boolean;
};

const initialValues: ContactFormValues = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  category: '',
  message: '',
  marketingConsent: false,
};

const inputClassName =
  'contact-field w-full rounded-[1.2rem] border border-[rgba(13,33,55,0.16)] bg-[var(--theme-paper)] px-5 py-4 text-base font-semibold text-[var(--theme-dark)] outline-none transition placeholder:text-[var(--theme-dark-soft)] focus:border-[var(--theme-accent)] focus:ring-4 focus:ring-[rgba(33,118,174,0.2)]';

const ContactFormSection = () => {
  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = ({
    target,
  }: React.ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >) => {
    setValues((current) => ({
      ...current,
      [target.name]:
        target instanceof HTMLInputElement && target.type === 'checkbox'
          ? target.checked
          : target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validator.isLength(values.name.trim(), { min: 2 })) {
      setError('Please enter your name.');
      return;
    }

    if (!validator.isEmail(values.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!validator.isMobilePhone(values.phone.trim(), 'any')) {
      setError('Please enter a valid phone number.');
      return;
    }

    if (!values.subject.trim() || !values.category || !values.message.trim()) {
      setError('Please fill in all the fields.');
      return;
    }

    if (!values.marketingConsent) {
      setError(
        'Please agree to receive marketing and communications from Docmoonlight.',
      );
      return;
    }

    setSubmitting(true);

    try {
      const response = await sendEmail({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        subject: values.subject.trim(),
        type: values.category,
        message: values.message.trim(),
        marketing_consent: values.marketingConsent,
      });

      if (!response.ok) {
        throw new Error('Failed to send your message. Please try again.');
      }

      setSuccess('Your message has been sent. We will get back to you soon.');
      setValues(initialValues);
    } catch (submitError: any) {
      setError(
        submitError?.message ||
          'Failed to send your message. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className='px-4 pb-14 pt-10 sm:px-6 lg:px-8 lg:pt-14'>
      <div className='mx-auto max-w-7xl'>
        <div className='relative overflow-hidden rounded-[2.75rem] border border-[rgba(13,33,55,0.1)] bg-[radial-gradient(circle_at_top_left,_rgba(33,118,174,0.12),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(158,200,232,0.15),_transparent_28%),linear-gradient(180deg,var(--theme-paper)_0%,var(--theme-surface)_100%)] px-6 py-8 shadow-[0_35px_100px_rgba(13,33,55,0.08)] md:px-10 md:py-10'>
          <div className='absolute left-0 top-0 h-52 w-52 rounded-full bg-[rgba(33,118,174,0.1)] blur-3xl' />
          <div className='absolute bottom-0 right-0 h-56 w-56 rounded-full bg-[rgba(158,200,232,0.15)] blur-3xl' />

          <div className='relative grid gap-8 lg:grid-cols-[minmax(280px,0.44fr)_minmax(0,0.56fr)] lg:items-stretch'>
            <div className='flex h-full flex-col rounded-[2rem] bg-[linear-gradient(135deg,var(--theme-dark)_0%,var(--theme-dark-soft)_100%)] p-7 text-[var(--theme-paper)] shadow-[0_20px_55px_rgba(13,33,55,0.18)]'>
              <div className='inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.24em] text-[var(--theme-accent-soft)]'>
                Contact
              </div>
              <h2 className='mt-5 text-[2rem] font-black leading-[1] tracking-tight md:text-5xl md:leading-[0.95]'>
                Let&apos;s make the next move clear.
              </h2>
              <p className='mt-5 text-lg leading-8 text-[var(--theme-light)]'>
                Reach out for coverage planning, center support, resident
                opportunities, or operational questions. We designed this page
                to feel more intentional than a standard contact form, while
                keeping the same core inputs you wanted.
              </p>

              <div className='mt-8 space-y-3'>
                {[
                  'Radiology coverage questions',
                  'Infusion center physician support',
                  'Resident and fellow opportunities',
                  'General operational inquiries',
                ].map((item) => (
                  <div
                    key={item}
                    className='rounded-[1.25rem] border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-[var(--theme-paper)]'
                  >
                    {item}
                  </div>
                ))}
              </div>

              <div className='mt-auto pt-8'>
                <p className='text-sm font-semibold uppercase tracking-[0.18em] text-[var(--theme-accent-soft)]'>
                  Response window
                </p>
                <p className='mt-2 text-lg font-bold text-[var(--theme-paper)]'>
                  Usually within 24-48 hours
                </p>
              </div>
            </div>

            <div className='rounded-[2rem] border border-[rgba(13,33,55,0.1)] bg-[var(--theme-paper)] p-6 shadow-[0_22px_60px_rgba(13,33,55,0.08)] backdrop-blur md:p-8'>
              <div className='scroll-mt-28 max-w-3xl'>
                <p
                  id='contact-form'
                  className='text-sm font-black uppercase tracking-[0.22em] text-[var(--theme-dark)]'
                >
                  Contact Us
                </p>
                <h3 className='mt-3 text-[1.75rem] font-black tracking-tight text-[var(--theme-dark)] md:text-4xl'>
                  Tell us what you need.
                </h3>
                <p className='mt-3 text-base font-semibold leading-7 text-[var(--theme-dark-soft)] md:text-lg'>
                  Fill out the form below and send the details directly to our
                  team.
                </p>
              </div>

              <form className='mt-8' onSubmit={handleSubmit}>
                <div className='grid gap-5 md:grid-cols-2'>
                  <input
                    className={inputClassName}
                    type='text'
                    name='name'
                    placeholder='Name'
                    value={values.name}
                    onChange={handleChange}
                    required
                  />
                  <input
                    className={inputClassName}
                    type='email'
                    name='email'
                    placeholder='Email'
                    value={values.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className='mt-5 space-y-5'>
                  <input
                    className={inputClassName}
                    type='tel'
                    name='phone'
                    placeholder='Phone Number'
                    value={values.phone}
                    onChange={handleChange}
                    required
                  />

                  <input
                    className={inputClassName}
                    type='text'
                    name='subject'
                    placeholder='Subject'
                    value={values.subject}
                    onChange={handleChange}
                    required
                  />

                  <select
                    name='category'
                    value={values.category}
                    onChange={handleChange}
                    className={`${inputClassName} select-dropdown pr-12 ${
                      values.category ? '' : 'contact-select-placeholder'
                    }`}
                    required
                  >
                    <option value=''>Select a Category...</option>
                    <option value='General inquiries'>General inquiries</option>
                    <option value='Radiology Centers'>Radiology Centers</option>
                    <option value='Infusion Centers'>Infusion Centers</option>
                    <option value='Residents & Fellows'>
                      Residents & Fellows
                    </option>
                    <option value='Technical'>Technical</option>
                    <option value='Payment'>Payment</option>
                  </select>

                  <textarea
                    rows={6}
                    name='message'
                    placeholder='Message'
                    value={values.message}
                    onChange={handleChange}
                    className={`${inputClassName} min-h-[180px] resize-y`}
                    required
                  />
                </div>

                <label className='mt-5 flex items-start gap-3 rounded-[1.25rem] bg-[var(--theme-surface)] px-4 py-4 text-left'>
                  <input
                    type='checkbox'
                    name='marketingConsent'
                    checked={values.marketingConsent}
                    onChange={handleChange}
                    className='mt-1 h-4 w-4 rounded border-[rgba(13,33,55,0.25)] text-[var(--theme-accent)] focus:ring-[var(--theme-accent)]'
                    required
                  />
                  <span className='text-sm font-semibold leading-6 text-[var(--theme-dark-soft)]'>
                    I agree to receive marketing and communications from{' '}
                    <strong>Docmoonlight</strong>
                  </span>
                </label>

                {error && (
                  <div className='mt-4 rounded-[1rem] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600'>
                    {error}
                  </div>
                )}

                {success && (
                  <div className='mt-4 rounded-[1rem] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700'>
                    {success}
                  </div>
                )}

                <button
                  type='submit'
                  disabled={submitting}
                  className='mt-6 inline-flex w-full items-center justify-center rounded-[1.1rem] bg-[linear-gradient(135deg,var(--theme-dark)_0%,var(--theme-dark-soft)_100%)] px-6 py-4 text-base font-black text-[var(--theme-accent-soft)] shadow-[0_18px_40px_rgba(13,33,55,0.24)] transition hover:translate-y-[-1px] hover:shadow-[0_24px_50px_rgba(13,33,55,0.28)] disabled:cursor-not-allowed disabled:opacity-70'
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>

              <div
                id='download-app'
                className='mt-8 rounded-[1.75rem] border border-[rgba(13,33,55,0.1)] bg-[linear-gradient(180deg,var(--theme-paper-warm)_0%,var(--theme-surface)_100%)] p-6'
              >
                <p className='text-sm font-black uppercase tracking-[0.22em] text-[var(--theme-accent)]'>
                  Join DocMoonlight
                </p>
                <h3 className='mt-3 text-2xl font-black tracking-tight text-[var(--theme-dark)] md:text-3xl'>
                  Download the app
                </h3>
                <p className='mt-3 max-w-2xl text-base font-semibold leading-7 text-[var(--theme-dark-soft)]'>
                  Get the DocMoonlight app on iOS or Android and explore
                  opportunities directly from your phone.
                </p>
                <div className='mt-5 flex flex-wrap items-center gap-3'>
                  <a
                    target='_blank'
                    href='https://play.google.com/store/apps/details?id=com.docmoonlight'
                    rel='noreferrer'
                    className='transition hover:opacity-95'
                  >
                    <Image
                      src='/images/google_play_btn.svg'
                      alt='Download on Google Play'
                      width={170}
                      height={55}
                      className='h-[55px] w-[170px] rounded-[10px] bg-white'
                    />
                  </a>
                  <a
                    target='_blank'
                    href='https://apps.apple.com/us/app/docmoonlight/id6448665833'
                    rel='noreferrer'
                    className='transition hover:opacity-95'
                  >
                    <Image
                      src='/images/app_store_btn.svg'
                      alt='Download on the App Store'
                      width={170}
                      height={55}
                      className='h-[55px] w-[170px] rounded-[10px] bg-white'
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
